// THE VEGETATION ATLAS, BAKED.
//
// These are the painters from apps/web/src/render/flora.ts, moved out of the
// browser and onto the rasteriser in ./raster.ts. Every silhouette is the one
// that shipped: the same blade taper, the same frond, the same tuft counts,
// the same tuned tone ranges, the same hard-won notes about why a leaf is
// 2–4% of a crown's radius and not 16–30%.
//
// WHAT CHANGED IS WHAT A TEXEL CARRIES. The sheet was strictly greyscale RGB
// with coverage in alpha, and it had to be: it is bound as `map`, a map
// multiplies all three channels, and the biome hue families own the palette
// (a first cut of the wave-10 sheet packed a translucency weight into green
// and turned every tussock in the flats into an orange butterfly). So ONE
// byte had to be three different things at once — the leaf's albedo, its
// thickness, and its relief — and the shader untangled them with a formula:
// "dark means thin".
//
// That formula is true of a margin and false of everything else. A canopy
// leaf painted a stop down to give a flat card an inside is not thinner, it
// is SHADED, and under "dark means thin" it glowed as hard as a backlit one.
// A rib painted bright is not proud because it is bright. The judges measured
// the consequence at r11: "flat untextured vector shapes at one value, median
// 40×40 frond span 130 against 202–205 in the reference".
//
// Baked, the sheet carries three independent channels and its coverage:
//
//   R  SHADE      the value the material multiplies into the leaf's albedo,
//                 including the depth darkening that gives a crown an inside.
//   G  THINNESS   1 at a margin, 0 at a stem. The transmission lobes read
//                 this directly instead of guessing it from R.
//   B  VEIN       geometric relief only — rib proud, lamina flat, margin low.
//                 The per-blade bump reads this, so a depth-darkened leaf no
//                 longer has a false gradient drawn round its outline.
//   A  COVERAGE   real alpha, anti-aliased at 4× in x and y, and with the
//                 colour BLED outward past the cut so no mip level drags a
//                 leaf's edge toward black.
//
// Nothing here imports three.js or the DOM. A painter returns numbers.

import { mulberry } from "./noise";
import { type Paint, Raster } from "./raster";

/** Cell names. Order here IS the atlas layout, row-major from the top-left. */
export const FLORA_CELLS = [
  // Row 0 — grasses and dry stalk: the ground-cover workhorses.
  "grassA",
  "grassB",
  "grassC",
  "straw",
  // Row 1 — the wet kit: ferns are §e's signature near-camera element.
  "fernA",
  "fernB",
  "fernSpray",
  "broadleaf",
  // Row 2 — verticals and the floor.
  "sedge",
  "rush",
  "litter",
  "moss",
  // Row 3 — canopy masses and the two distance impostors.
  "canopyA",
  "canopyB",
  "clumpFar",
  "treeFar",
] as const;

export type FloraCell = (typeof FLORA_CELLS)[number];

export const FLORA_GRID = 4;
/**
 * Texels per cell. 512 puts a 0.5 m tussock at ~1 mm/texel, which still
 * resolves a midrib when the card fills 400 px of a 1080p frame.
 *
 * UNCHANGED BY THE MOVE OFFLINE, deliberately. There is no time budget here,
 * so 1024 was available — and it is the wrong buy. The atlas is the one sheet
 * in the game that needs a genuine alpha channel, which means UASTC, which
 * means eight bits per texel before supercompression: a 4096² atlas is 2.7 MB
 * against 700 kB at 2048², for detail below the size of the features the
 * painters draw. What the offline budget is spent on instead is the number of
 * strokes — the canopy masses paint 900 leaves apiece and the moss card 300
 * blades, at a cost nobody waits for.
 */
export const FLORA_CELL_PX = 512;
export const FLORA_ATLAS_PX = FLORA_GRID * FLORA_CELL_PX;

// ---------------------------------------------------------------------------
// Painting primitives.
//
// Everything below draws in a UNIT CELL: x and y run 0..1 with y UP and the
// plant's root at (0.5, 0). The caller installs a transform that flips the
// y axis, so a "blade grows upward" here really does grow toward the top of
// the cell — which is where the card geometry's v=1 edge lands.

type Ctx = Raster;

/**
 * A leaf paint. `shade` is the painted value; `thin` and `vein` default to
 * the two things a lamina is — thin, and flat.
 */
const paint = (shade: number, thin = 0.78, vein = 0.5): Paint => ({
  shade: Math.max(0, Math.min(1, shade)),
  thin: Math.max(0, Math.min(1, thin)),
  vein: Math.max(0, Math.min(1, vein)),
});

/** Quadratic spine sample and its tangent — every blade, frond rachis and
 *  stem in the kit is one of these, so they all arc the same way. */
function spine(
  x0: number,
  y0: number,
  len: number,
  lean: number,
  bend: number,
  t: number,
): [number, number, number, number] {
  // P0 root, P1 half way along the initial heading, P2 the tip after the
  // heading has rotated by `bend`. A blade that leaves its base straight is
  // a spike; the arc is most of what makes a tuft read as grown.
  const h = len * 0.5;
  const p1x = x0 + Math.sin(lean) * h;
  const p1y = y0 + Math.cos(lean) * h;
  const p2x = p1x + Math.sin(lean + bend) * h;
  const p2y = p1y + Math.cos(lean + bend) * h;
  const it = 1 - t;
  const x = it * it * x0 + 2 * it * t * p1x + t * t * p2x;
  const y = it * it * y0 + 2 * it * t * p1y + t * t * p2y;
  const dx = 2 * it * (p1x - x0) + 2 * t * (p2x - p1x);
  const dy = 2 * it * (p1y - y0) + 2 * t * (p2y - p1y);
  const inv = 1 / Math.max(1e-5, Math.hypot(dx, dy));
  return [x, y, dx * inv, dy * inv];
}

interface BladeOpts {
  /** Half-width at the widest point, cell fractions. */
  width: number;
  /** Where along the blade the swell peaks (0 = at the root). */
  swell?: number;
  /** Shading channel at the margin and along the rib. */
  edge?: number;
  rib?: number;
  /** Extra darkening of the last fifth (a tip is thin, and thin is dark
   *  by rule 2, which is what makes it the part that glows when backlit). */
  tipDark?: number;
  /** Segments along the spine. 12 is enough for a 512 px cell. */
  segments?: number;
  /**
   * Venation. Pairs of lateral veins struck off the rib, and a darkened band
   * just inside the margin.
   *
   * A vein is a ridge a millimetre proud of the lamina; at a metre from the
   * lens that ridge is what catches a grazing sun and puts a highlight ON a
   * leaf rather than only on the sky behind it. 0 disables them, which is
   * what a canopy card wants — its leaves are eleven texels across and a vein
   * there is sub-texel noise.
   */
  veins?: number;
  /** Serration depth as a fraction of the half-width. A margin that is a
   *  smooth curve reads as vinyl; the reference's ferns are toothed, and
   *  the teeth are what break the silhouette into catchable edges. */
  serrate?: number;
  /**
   * THINNESS of the lamina and of the rib. This is the pair the old sheet
   * could not express: a frond's RACHIS is drawn with the same primitive as
   * its pinnae, and a rachis is a stick. Default is a leaf.
   */
  thin?: readonly [number, number];
}

/** One tapered, arced leaf. Drawn as a filled polygon so the silhouette is
 *  a real cut rather than a stroked line's rounded cap. */
function blade(
  ctx: Ctx,
  x0: number,
  y0: number,
  len: number,
  lean: number,
  bend: number,
  o: BladeOpts,
): void {
  const seg = o.segments ?? 12;
  const swell = o.swell ?? 0.16;
  const edge = o.edge ?? 0.5;
  const rib = o.rib ?? 0.9;
  const tipDark = o.tipDark ?? 0.45;
  const [thinLamina, thinRib] = o.thin ?? [0.78, 0.14];
  const half = (t: number): number => {
    // Swells just above the root, tapers to a point. The exponent keeps the
    // taper concave so the blade stays wide most of its length and then
    // closes fast — a linear taper reads as a triangle, not as a leaf.
    const a = t < swell ? t / swell : 1 - (t - swell) / (1 - swell);
    return o.width * a ** 0.55;
  };
  const serrate = o.serrate ?? 0;
  const veins = o.veins ?? 0;
  /** Tooth profile along the margin — one notch per spine segment. */
  const tooth = (t: number): number =>
    serrate === 0 ? 1 : 1 - serrate * (0.5 + 0.5 * Math.cos(t * seg * Math.PI));
  const fill = (widthScale: number, p: Paint, teeth = false): void => {
    ctx.beginPath();
    for (let i = 0; i <= seg; i++) {
      const t = i / seg;
      const [x, y, tx, ty] = spine(x0, y0, len, lean, bend, t);
      const w = half(t) * widthScale * (teeth ? tooth(t) : 1);
      if (i === 0) ctx.moveTo(x + ty * w, y - tx * w);
      else ctx.lineTo(x + ty * w, y - tx * w);
    }
    for (let i = seg; i >= 0; i--) {
      const t = i / seg;
      const [x, y, tx, ty] = spine(x0, y0, len, lean, bend, t);
      const w = half(t) * widthScale * (teeth ? tooth(t) : 1);
      ctx.lineTo(x - ty * w, y + tx * w);
    }
    ctx.closePath();
    ctx.fill(p);
  };
  // Lamina first, then the rib over it. Two fills, one silhouette. The lamina
  // sits BELOW the rib in the vein channel, which is the relief a leaf
  // actually has and which the old sheet could only imply through its tone.
  fill(1, paint(edge, thinLamina, 0.42), serrate > 0);
  // A DARKENED MARGIN BAND before the rib: the lamina is thinnest where it
  // ends, so this is the strip the transmission lobe lights up when the sun
  // is behind the plant. Now it is thin BECAUSE IT IS PAINTED THIN, rather
  // than because it happens to be dark.
  if (veins > 0) {
    fill(0.88, paint(edge * 1.16, thinLamina * 0.86, 0.5), serrate > 0);
  }
  fill(0.42, paint(rib, thinRib, 0.86));
  // Lateral venation, struck off the rib and stopping short of the margin.
  // Clipped to the blade by `atop` so a vein can never hang in the air off
  // the tip of the leaf it belongs to.
  if (veins > 0) {
    const vp = paint(rib * 1.12, thinRib * 1.6, 0.97);
    const lw = Math.max(0.0009, o.width * 0.09);
    const pairs = Math.max(3, Math.round(veins));
    for (let i = 1; i <= pairs; i++) {
      const t = 0.1 + (i / (pairs + 1)) * 0.82;
      const [x, y, tx, ty] = spine(x0, y0, len, lean, bend, t);
      const [x2, y2] = spine(x0, y0, len, lean, bend, Math.min(1, t + 0.16));
      const w = half(t) * 0.86;
      for (const side of [1, -1]) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x2 + ty * w * side, y2 - tx * w * side);
        ctx.stroke(vp, lw, { atop: true, round: false });
      }
    }
  }
  // The tip goes back to margin tone: thin, and therefore translucent. It is
  // the thinnest thing on the sheet, which is why it is the part of a fern
  // that lights up first when the sun goes behind it.
  {
    const [tipX, tipY] = spine(x0, y0, len, lean, bend, 0.78);
    const [endX, endY] = spine(x0, y0, len, lean, bend, 1);
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(endX, endY);
    ctx.stroke(
      paint(edge * tipDark + edge * (1 - tipDark) * 0.55, Math.min(1, thinLamina * 1.25), 0.3),
      o.width * 2.1,
      { atop: true },
    );
  }
}

/** One ovate leaf on a short petiole — the broadleaf and litter primitive. */
function leaf(
  ctx: Ctx,
  x: number,
  y: number,
  len: number,
  ang: number,
  width: number,
  tone: number,
  thin = 0.76,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(-ang);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(width, len * 0.28, width * 0.72, len * 0.82, 0, len);
  ctx.bezierCurveTo(-width * 0.72, len * 0.82, -width, len * 0.28, 0, 0);
  ctx.closePath();
  ctx.fill(paint(tone, thin, 0.44));
  // Veins only when the leaf is big enough to carry them. A canopy card
  // paints ~900 leaves at eleven texels apiece: at that size the midrib is
  // sub-texel, and drawing it anyway both costs the generation budget and
  // washes the whole crown a stop paler.
  if (len < 0.05) {
    ctx.restore();
    return;
  }
  // Midrib and two veins: the difference between a leaf and a green almond.
  const vp = paint(tone * 1.42, thin * 0.2, 0.95);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(0, len * 0.94);
  ctx.stroke(vp, width * 0.14, { atop: true });
  for (const f of [0.34, 0.58]) {
    ctx.beginPath();
    ctx.moveTo(0, len * f);
    ctx.lineTo(width * 0.62, len * (f + 0.2));
    ctx.stroke(vp, width * 0.075, { atop: true });
    ctx.beginPath();
    ctx.moveTo(0, len * f);
    ctx.lineTo(-width * 0.62, len * (f + 0.2));
    ctx.stroke(vp, width * 0.075, { atop: true });
  }
  ctx.restore();
}

/** A pinnate frond: rachis plus opposed pinnae falling off toward the tip.
 *  This shape is the whole reason the fen gets a fern kind — it is the one
 *  silhouette in the plant kingdom nobody mistakes for a generic bush, and
 *  it is what fills the bottom quarter of the reference's forest frames. */
function frond(
  ctx: Ctx,
  x0: number,
  y0: number,
  len: number,
  lean: number,
  bend: number,
  scale: number,
  rng: () => number,
): void {
  const seg = 26;
  // The rachis: a thin bright STEM, drawn first so pinnae overlap it. It is
  // painted opaque now — a rachis is a stick, and under "dark means thin" a
  // bright stick was read as the least translucent thing on the card by
  // accident rather than by statement.
  blade(ctx, x0, y0, len, lean, bend, {
    width: 0.012 * scale,
    swell: 0.05,
    edge: 0.82,
    rib: 0.95,
    tipDark: 1,
    segments: 16,
    thin: [0.1, 0.04],
  });
  for (let i = 1; i <= seg; i++) {
    const t = i / (seg + 1);
    const [x, y, tx, ty] = spine(x0, y0, len, lean, bend, t);
    // Pinnae are longest a third of the way up and shrink to nothing at the
    // tip; the ±jitter keeps opposed pairs from reading as a fishbone.
    const grow = Math.min(1, t / 0.24);
    const pl = len * 0.3 * scale * grow * (1 - t) ** 0.75 * (0.85 + rng() * 0.3);
    if (pl < 0.004) continue;
    const along = Math.atan2(tx, ty);
    for (const side of [1, -1]) {
      const off = side * 0.0035 * scale;
      blade(
        ctx,
        x + ty * off,
        y - tx * off,
        pl,
        along + side * (0.95 + rng() * 0.24),
        -side * (0.45 + rng() * 0.3),
        {
          width: pl * 0.19,
          swell: 0.24,
          // A frond TWISTS along its rachis, so one file of pinnae presents
          // its face to the light and the other its edge. Splitting the two
          // sides by a stop and a half is what gives it depth.
          edge: (side > 0 ? 0.3 : 0.56) + rng() * 0.16,
          rib: side > 0 ? 0.74 : 0.92,
          tipDark: 0.4,
          segments: 7,
          // A pinna is 30–60 texels long in this cell: big enough for three
          // vein pairs and a toothed margin, which is what turns the frond
          // from a painted silhouette into a surface with relief.
          veins: 3,
          serrate: 0.16,
          // BOTH FILES ARE EQUALLY THIN. Only their SHADE differs, because
          // the one turned away from the key is darker, not thicker — and
          // that is precisely the distinction the greyscale sheet could not
          // draw. It is why the shaded half of every frond in the fen used
          // to refuse to glow when the sun was behind it.
          thin: [0.86, 0.16],
        },
      );
    }
  }
}

// ---------------------------------------------------------------------------
// The cells themselves.

type Painter = (ctx: Ctx, rng: () => number) => void;

/** A fan of blades from a common root — the grass primitive. `spread` is the
 *  half-angle of the fan, `droop` how far the outermost blades fall over. */
function tuftPainter(
  count: number,
  height: [number, number],
  spread: number,
  droop: number,
  width: number,
  tone: [number, number],
  seedHeads = 0,
): Painter {
  return (ctx, rng) => {
    // Back-to-front by height so the tall blades sit in front: painting a
    // clump in one flat tone is what made the wave-10 kit read as a decal.
    const blades: { lean: number; len: number; dark: number }[] = [];
    for (let i = 0; i < count; i++) {
      const u = (i + rng() * 0.7) / count - 0.5;
      blades.push({
        lean: u * spread * 2 + (rng() - 0.5) * 0.16,
        len: height[0] + rng() * (height[1] - height[0]),
        // Depth cue: a third of the blades are painted a stop down so the
        // clump has an inside. Free parallax, and it survives every mip.
        dark: rng() < 0.34 ? 0.62 : 1,
      });
    }
    blades.sort((a, b) => a.len - b.len);
    for (const b of blades) {
      const bend =
        -Math.sign(b.lean || 1) * droop * (0.4 + Math.abs(b.lean) / spread) * (0.6 + rng() * 0.8);
      blade(ctx, 0.5 + b.lean * 0.09, 0.005, b.len, b.lean, bend, {
        width: width * (0.75 + rng() * 0.5),
        swell: 0.1 + rng() * 0.12,
        edge: (tone[0] + rng() * (tone[1] - tone[0])) * b.dark,
        rib: Math.min(1, (tone[1] + 0.1) * b.dark),
        tipDark: 0.4,
        // A grass blade in shadow is exactly as thin as one in the sun. The
        // depth darkening multiplies `edge` and `rib` above and stops there.
        thin: [0.84, 0.2],
      });
    }
    for (let i = 0; i < seedHeads; i++) {
      const lean = (rng() - 0.5) * spread * 1.2;
      const bend = -lean * 0.55;
      const len = height[1] * (0.86 + rng() * 0.14);
      // The STALK first, then the head on its tip. Painting the head alone
      // left a row of florets hanging in the sky above every tuft, because
      // nothing guaranteed a blade reached that high.
      blade(ctx, 0.5 + lean * 0.05, 0.005, len, lean, bend, {
        width: 0.007,
        swell: 0.06,
        edge: 0.66,
        rib: 0.88,
        tipDark: 1,
        segments: 10,
        thin: [0.14, 0.06],
      });
      // A seed head is a stack of short florets, not an ellipse. Florets are
      // the thinnest thing a grass has, and a seed head rimmed by a low sun
      // is the single most recognisable silhouette in a dry meadow.
      for (let k = 0; k < 10; k++) {
        const f = k / 10;
        const [x, y, tx, ty] = spine(0.5 + lean * 0.05, 0.005, len, lean, bend, 1 - f * 0.16);
        const along = Math.atan2(tx, ty);
        blade(ctx, x, y, len * (0.1 - f * 0.04), along + (rng() - 0.5) * 1.7, 0.5, {
          width: 0.0055,
          edge: 0.7,
          rib: 0.94,
          tipDark: 0.6,
          segments: 5,
          thin: [0.95, 0.5],
        });
      }
    }
  };
}

/**
 * A rough ball of small leaves on visible twigs, centred in the cell.
 *
 * LEAF SIZE IS THE WHOLE ARGUMENT HERE. A real leaf is 5–15 cm, so on a 4 m
 * crown card it is 2–4% of the radius, and covering a crown at that size
 * takes not eighty of them but eight hundred. Fills are cheap and this runs
 * once, offline.
 */
function canopyMass(
  ctx: Ctx,
  rng: () => number,
  radius: number,
  leaves: number,
  gap: number,
): void {
  // Twigs first, then the leaves over them.
  for (let i = 0; i < 7; i++) {
    const a = -0.95 + (i / 6) * 1.9 + (rng() - 0.5) * 0.25;
    blade(ctx, 0.5, 0.5 - radius * 0.9, radius * (0.8 + rng() * 0.5), a, -a * 0.45, {
      width: 0.006,
      swell: 0.05,
      edge: 0.32,
      rib: 0.5,
      tipDark: 1,
      segments: 8,
      thin: [0.06, 0.02],
    });
  }
  for (let i = 0; i < leaves; i++) {
    const a = rng() * Math.PI * 2;
    // Hollow-ish: leaves crowd the shell of the mass, which is where a real
    // crown carries them, and it leaves sky holes through the middle.
    const r = radius * (gap + (1 - gap) * Math.sqrt(rng())) * (0.62 + 0.38 * Math.abs(Math.sin(a)));
    const x = 0.5 + Math.cos(a) * r;
    const y = 0.5 + Math.sin(a) * r * 0.92;
    const size = radius * (0.045 + rng() * 0.042);
    // Depth: the far side of the crown is painted down a stop, which is what
    // gives a flat card a lit face and a shaded interior.
    //
    // AND IT IS EXACTLY AS THIN EITHER WAY. This is the correction the
    // separated channels exist for: under "dark means thin" these 44% of
    // leaves were the MOST translucent texels on the whole sheet, so a
    // backlit crown lit its own shadowed interior brighter than its lit
    // face — the "flattens to bone" failure the transmission term's own
    // comment records from r11-t5.
    const dark = rng() < 0.44 ? 0.55 : 1;
    leaf(ctx, x, y, size, rng() * Math.PI * 2, size * 0.42, (0.46 + rng() * 0.36) * dark, 0.72);
  }
}

const PAINTERS: Record<FloraCell, Painter> = {
  // --- Grasses. Three silhouettes so a carpet never stamps one shape: a
  // dense mid tussock, a wide low mat, and a sparse tall stand with seed.
  grassA: tuftPainter(19, [0.48, 0.97], 0.82, 0.9, 0.021, [0.42, 0.72]),
  grassB: tuftPainter(24, [0.3, 0.66], 1.15, 1.2, 0.024, [0.4, 0.7]),
  grassC: tuftPainter(14, [0.6, 0.96], 0.55, 0.55, 0.016, [0.46, 0.76], 4),
  // Dry stalk: pale, snapped, half of it lying over. The dry biomes' bush.
  straw: (ctx, rng) => {
    tuftPainter(17, [0.34, 0.92], 1.15, 1.35, 0.013, [0.66, 0.95], 3)(ctx, rng);
    // Broken stems: a straw clump's read is that some of it has failed. The
    // bend stays under ~1.1 rad — at 2.1 the stems closed into hoops and the
    // cell painted a bird's nest.
    for (let i = 0; i < 6; i++) {
      const lean = 0.5 + rng() * 0.9;
      const side = i % 2 === 0 ? 1 : -1;
      blade(
        ctx,
        0.5 + (rng() - 0.5) * 0.14,
        0.005,
        0.3 + rng() * 0.34,
        side * lean,
        -side * (0.7 + rng() * 0.4),
        {
          width: 0.011,
          edge: 0.58,
          rib: 0.8,
          tipDark: 0.5,
          // Dead straw is a hollow tube and the most translucent thing in a
          // dry biome — the reason a stubble field goes to fire at sunset.
          thin: [0.92, 0.4],
        },
      );
    }
  },

  // --- Ferns. §e's near-camera hero: one big arching frond per card, so a
  // five-card clump is a fern bush at forty triangles.
  fernA: (ctx, rng) => {
    frond(ctx, 0.5, 0.01, 0.93, 0.05, -0.34, 1, rng);
  },
  fernB: (ctx, rng) => {
    frond(ctx, 0.46, 0.01, 0.78, -0.26, 0.5, 1.15, rng);
  },
  fernSpray: (ctx, rng) => {
    for (const [x, len, lean, bend] of [
      [0.5, 0.72, 0.02, -0.3],
      [0.36, 0.55, -0.42, 0.45],
      [0.64, 0.5, 0.46, -0.5],
    ] as const) {
      frond(ctx, x, 0.01, len, lean, bend, 0.85, rng);
    }
  },
  // --- Broadleaf spray: the wet-shade understorey, and the one card that
  // gives a shrub its solid mass instead of another handful of blades.
  broadleaf: (ctx, rng) => {
    const stems: [number, number, number, number][] = [];
    for (let i = 0; i < 13; i++) {
      const lean = (i / 12 - 0.5) * 1.5 + (rng() - 0.5) * 0.3;
      const len = 0.3 + rng() * 0.5;
      stems.push([lean, len, 0.1 + rng() * 0.16, rng()]);
    }
    stems.sort((a, b) => a[1] - b[1]);
    for (const [lean, len, size, r] of stems) {
      const bend = -Math.sign(lean || 1) * 0.5 * (0.5 + r);
      blade(ctx, 0.5, 0.005, len, lean, bend, {
        width: 0.008,
        edge: 0.8,
        rib: 0.9,
        tipDark: 1,
        segments: 8,
        thin: [0.12, 0.05],
      });
      const [x, y, tx, ty] = spine(0.5, 0.005, len, lean, bend, 1);
      leaf(ctx, x, y, size, Math.atan2(tx, ty) + (r - 0.5) * 0.4, size * 0.34, 0.5 + r * 0.26, 0.8);
      // A second leaf half way up the stem — bare stems read as wire.
      const [mx, my, mtx, mty] = spine(0.5, 0.005, len, lean, bend, 0.62);
      leaf(
        ctx,
        mx,
        my,
        size * 0.7,
        Math.atan2(mtx, mty) + (r - 0.5) * 1.1,
        size * 0.24,
        0.46 + r * 0.24,
        0.8,
      );
    }
  },

  // --- Verticals. Sedge is the fen's fence line; rush is the wet tussock
  // that grows out of standing water.
  sedge: (ctx, rng) => {
    tuftPainter(17, [0.68, 1.0], 0.44, 0.5, 0.018, [0.44, 0.74], 5)(ctx, rng);
  },
  rush: (ctx, rng) => {
    tuftPainter(26, [0.5, 0.98], 0.34, 0.3, 0.011, [0.5, 0.8], 6)(ctx, rng);
  },

  // --- The floor. `litter` lies flat under the camera; `moss` is the low
  // creeper that closes the last gaps between tussocks.
  litter: (ctx, rng) => {
    for (let i = 0; i < 78; i++) {
      const x = 0.04 + rng() * 0.92;
      const y = 0.04 + rng() * 0.92;
      if (rng() < 0.42) {
        // Twig — wood, and therefore opaque. On the old sheet a twig was
        // painted dark, so it was the brightest thing on the fen floor the
        // moment the sun went behind the treeline.
        blade(ctx, x, y, 0.12 + rng() * 0.3, rng() * Math.PI * 2, (rng() - 0.5) * 0.8, {
          width: 0.006 + rng() * 0.008,
          swell: 0.4,
          edge: 0.3 + rng() * 0.24,
          rib: 0.58,
          tipDark: 1,
          segments: 6,
          thin: [0.05, 0.02],
        });
      } else {
        leaf(
          ctx,
          x,
          y,
          0.12 + rng() * 0.2,
          rng() * Math.PI * 2,
          0.034 + rng() * 0.045,
          0.38 + rng() * 0.48,
          // Fallen leaf mould: dry, and the most translucent litter there is.
          0.88,
        );
      }
    }
  },
  moss: (ctx, rng) => {
    // Dense enough to read as a mat rather than as specks: this card is the
    // last thing standing between the fen floor and "bare terrain texture in
    // frame", so its whole job is coverage.
    for (let i = 0; i < 300; i++) {
      const a = rng() * Math.PI * 2;
      const r = Math.sqrt(rng()) * 0.48;
      const x = 0.5 + Math.cos(a) * r;
      const y = 0.5 + Math.sin(a) * r * 0.94;
      blade(ctx, x, y, 0.06 + rng() * 0.11, rng() * Math.PI * 2, (rng() - 0.5) * 1.4, {
        width: 0.008,
        edge: 0.4 + rng() * 0.32,
        rib: 0.78,
        tipDark: 0.5,
        segments: 5,
        thin: [0.8, 0.3],
      });
    }
  },

  // --- Canopy masses: a bough's worth of leaves, for the fen trees. Painted
  // as a cloud of small leaves with the twigs showing through, so the gaps
  // are real gaps and the sky comes through them.
  canopyA: (ctx, rng) => canopyMass(ctx, rng, 0.45, 900, 0.08),
  canopyB: (ctx, rng) => canopyMass(ctx, rng, 0.4, 720, 0.13),

  // --- The two impostors. Everything past the LOD radius is one of these
  // crossed onto two quads, which is what pays for the near-field density.
  clumpFar: (ctx, rng) => {
    // A whole tussock's silhouette, wide and low, drawn from the same blade
    // primitive so the swap at distance is a resolution change, not a
    // species change. It fills the cell: an impostor that paints 40% of its
    // texels is an impostor at 40% of the resolution it was budgeted.
    tuftPainter(46, [0.36, 0.9], 1.3, 1.3, 0.021, [0.42, 0.74], 3)(ctx, rng);
    for (let i = 0; i < 4; i++) {
      frond(
        ctx,
        0.22 + rng() * 0.56,
        0.01,
        0.44 + rng() * 0.26,
        (rng() - 0.5) * 1.5,
        (rng() - 0.5) * 0.9,
        0.62,
        rng,
      );
    }
  },
  treeFar: (ctx, rng) => {
    // Trunk: a dark tapered wedge with three boughs, then the crown over it.
    // Bark tone deliberately near the crown's dark end — at impostor
    // distance the fog has eaten most of the value separation anyway, and a
    // black stick against a pale crown reads as a crack in the image.
    const bark = paint(0.34, 0.02, 0.62);
    ctx.beginPath();
    ctx.moveTo(0.448, 0);
    ctx.lineTo(0.552, 0);
    ctx.lineTo(0.521, 0.66);
    ctx.lineTo(0.479, 0.66);
    ctx.closePath();
    ctx.fill(bark);
    for (const [y, dir, len] of [
      [0.27, 1, 0.28],
      [0.4, -1, 0.24],
      [0.53, 1, 0.18],
    ] as const) {
      ctx.save();
      ctx.translate(0.5, y);
      ctx.rotate(-dir * 1.05);
      ctx.beginPath();
      ctx.moveTo(-0.009, 0);
      ctx.lineTo(0.009, 0);
      ctx.lineTo(0.009, len);
      ctx.lineTo(-0.009, len);
      ctx.closePath();
      ctx.fill(bark);
      ctx.restore();
    }
    // Crown: four overlapping masses at different heights so the silhouette
    // is ragged rather than the lollipop a single ball paints.
    for (const [cx, cy, r, n] of [
      [0.5, 0.66, 0.31, 520],
      [0.26, 0.53, 0.21, 250],
      [0.74, 0.58, 0.22, 270],
      [0.56, 0.83, 0.19, 220],
    ] as const) {
      ctx.save();
      ctx.translate(cx - 0.5, cy - 0.5);
      canopyMass(ctx, rng, r, n, 0.12);
      ctx.restore();
    }
  },
};

/**
 * Paint the whole atlas.
 *
 * Every cell is clipped to its own square so a stray stroke can never bleed
 * into its neighbour — an atlas's one failure mode, and it shows up as a
 * ghost leaf hanging in space off every instance in the game.
 */
export function bakeFloraAtlas(cellPx = FLORA_CELL_PX): Uint8Array {
  const size = FLORA_GRID * cellPx;
  const r = new Raster(size, size);
  FLORA_CELLS.forEach((cell, i) => {
    const cx = (i % FLORA_GRID) * cellPx;
    const cy = Math.floor(i / FLORA_GRID) * cellPx;
    r.clipRect(cx, cy, cellPx, cellPx);
    // Unit cell with y up, root at (0.5, 0).
    r.setTransform(cellPx, 0, 0, -cellPx, cx, cy + cellPx);
    PAINTERS[cell](r, mulberry(0xf10a + i * 7919));
    r.restore();
  });
  // Bleed the colour outward past the alpha cut before quantising — see the
  // note on `toRGBA8`. Eight texels covers every mip level the cards are ever
  // sampled at.
  return r.toRGBA8(8);
}

/** [u0, v0, u1, v1] of a cell in atlas UV space, v up, with the mip inset. */
export function floraCellRect(cell: FloraCell): readonly [number, number, number, number] {
  const i = FLORA_CELLS.indexOf(cell);
  const col = i % FLORA_GRID;
  // Atlas rows are painted top-down; UV v runs up, so flip the row.
  const row = FLORA_GRID - 1 - Math.floor(i / FLORA_GRID);
  // An INSET on every cell rect, not just a nicety: mip level 4 of this sheet
  // averages 16 texels, and without the inset a fern's tip borrows alpha from
  // the grass card below it and mid-distance foliage grows a faint halo.
  const pad = 3 / FLORA_ATLAS_PX;
  return [
    col / FLORA_GRID + pad,
    row / FLORA_GRID + pad,
    (col + 1) / FLORA_GRID - pad,
    (row + 1) / FLORA_GRID - pad,
  ];
}
